<template>
  <section data-cy="equipment-loader-display">
    <p data-cy="downloading-status">Downloading:</p>
    <ProgressBar
      :value="progress"
      :show-value="false"
      class="my-2"
      data-cy="progress-bar"
    />
    <p
      v-for="(fetching, key) in fetchingStatuses"
      :key="key"
      :data-cy="`${key}-status`"
    >
      {{ key }} -
      <icon
        :icon="fetching ? ['far', 'check-circle'] : ['far', 'spinner-third']"
        :spin="!fetching"
        :data-cy="`${key}-icon`"
      />
    </p>
  </section>
</template>

<script setup lang="ts">
import { computed, watchEffect } from "vue";
import ProgressBar from "primevue/progressbar";
import {
  useAllEnumsQuery,
  useCablesQuery,
  useCasingsQuery,
  useIntakesQuery,
  useMotorLeadExtensionsQuery,
  useMotorsQuery,
  usePumpsQuery,
  useSealsQuery,
  useSealMotorOilQuery,
  useSensorsQuery,
  useStepDownTransformersQuery,
  useStepUpTransformersQuery,
  useSwitchboardsQuery,
  useTubingsQuery,
  useVariableSpeedDrivesQuery,
  useYToolsQuery
} from "gql/api";
import { defaultCasingVars } from "@/models/equipment/casings/predicates";
import { defaultPumpVars } from "@/models/equipment/pumps/predicates";
import { defaultCableVars } from "@/models/equipment/cables/predicates";
import { defaultMleVars } from "@/models/equipment/mle/predicates";
import { defaultIntakeVars } from "@/models/equipment/intake/predicates";
import { defaultMotorVars } from "@/models/equipment/motors/predicates";
import { defaultSealVars } from "@/models/equipment/seals/predicates";
import { defaultDownTransVars } from "@/models/equipment/step_down_transformer/predicates";
import { defaultSensorVars } from "@/models/equipment/sensors/predicates";
import { defaultUpTransVars } from "@/models/equipment/step_up_transformer/predicates";
import { defaultSwitchboardVars } from "@/models/equipment/switchboards/predicates";
import { defaultTubingVars } from "@/models/equipment/tubings/predicates";
import { defaultVsdVars } from "@/models/equipment/vsd/predicates";
import { defaultYToolVars } from "@/models/equipment/y_tool/predicates";
import {
  defaultSealMotorOilVars,
  scenarioDefaultSealMotorOilVars
} from "@/models/equipment/seal_motor_oil/predicates";

const emit = defineEmits<{ (e: "complete"): void }>();

const enums = useAllEnumsQuery();
const cables = useCablesQuery({ variables: defaultCableVars });
const casings = useCasingsQuery({ variables: defaultCasingVars });
const intakes = useIntakesQuery({ variables: defaultIntakeVars });
const motors = useMotorsQuery({ variables: defaultMotorVars });
const pumps = usePumpsQuery({ variables: defaultPumpVars });
const seals = useSealsQuery({ variables: defaultSealVars });
const sensors = useSensorsQuery({ variables: defaultSensorVars });
const tubings = useTubingsQuery({ variables: defaultTubingVars });
const mles = useMotorLeadExtensionsQuery({ variables: defaultMleVars });
const vsds = useVariableSpeedDrivesQuery({ variables: defaultVsdVars });
const uptrans = useStepUpTransformersQuery({ variables: defaultUpTransVars });
const downtrans = useStepDownTransformersQuery({
  variables: defaultDownTransVars
});
const switchboards = useSwitchboardsQuery({
  variables: defaultSwitchboardVars
});
const sealMotorOils = useSealMotorOilQuery({
  variables: defaultSealMotorOilVars
});
const defaultSealMotorOil = useSealMotorOilQuery({
  variables: scenarioDefaultSealMotorOilVars
});
const yTools = useYToolsQuery({ variables: defaultYToolVars });

const fetchingStatuses = computed(() => ({
  Enums: !enums.stale.value && !enums.fetching.value,
  Cables: !cables.stale.value && !cables.fetching.value,
  Casings: !casings.stale.value && !casings.fetching.value,
  Intakes: !intakes.stale.value && !intakes.fetching.value,
  "Motor lead extensions": !mles.stale.value && !mles.fetching.value,
  Motors: !motors.stale.value && !motors.fetching.value,
  Pumps: !pumps.stale.value && !pumps.fetching.value,
  Seals: !seals.stale.value && !seals.fetching.value,
  "Seals Motor Oils":
    !sealMotorOils.stale.value && !sealMotorOils.fetching.value,
  "Default Seal Motor Oil":
    !defaultSealMotorOil.stale.value && !defaultSealMotorOil.fetching.value,
  Sensors: !sensors.stale.value && !sensors.fetching.value,
  "Step up transformers": !uptrans.stale.value && !uptrans.fetching.value,
  "Step down transformers": !downtrans.stale.value && !downtrans.fetching.value,
  Switchboards: !switchboards.stale.value && !switchboards.fetching.value,
  Tubings: !tubings.stale.value && !tubings.fetching.value,
  "Variable speed drives": !vsds.stale.value && !vsds.fetching.value,
  "Y tools": !yTools.stale.value && !yTools.fetching.value
}));

const progress = computed(() => {
  const operations = Object.values(fetchingStatuses.value);
  const total = operations.length;
  const totalComplete = operations.reduce(
    (sum, isFetching) => sum + Number(isFetching),
    0
  );

  return Math.round((totalComplete / total) * 100);
});

watchEffect(() => {
  if (progress.value === 100) {
    emit("complete");
  }
});
</script>
